/**
 * Тема интерфейса: светлая, тёмная или системная.
 *
 * Тёмная тема — с первого дня, а не «дополнительная фича»
 * (docs/10-ui-ux.md). Выбор хранится в localStorage; значение 'system'
 * означает «следовать настройке ОС», и тогда атрибут data-theme не ставится —
 * решает CSS через prefers-color-scheme.
 */

export const THEMES = ['light', 'dark', 'system'] as const
export type Theme = (typeof THEMES)[number]

const STORAGE_KEY = 'theme'

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value)
}

export function getStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isTheme(stored) ? stored : 'system'
  } catch {
    // Приватный режим может запрещать localStorage — это не повод падать.
    return 'system'
  }
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', theme)
  }
}

export function setTheme(theme: Theme): void {
  applyTheme(theme)
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Тема не сохранится между сессиями, но текущая работает.
  }
}

/** Применить сохранённую тему до первого рендера, чтобы не мигало. */
export function applyStoredTheme(): Theme {
  const theme = getStoredTheme()
  applyTheme(theme)
  return theme
}

/** Какая тема фактически показывается сейчас. */
export function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme
  const prefersDark =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  return prefersDark ? 'dark' : 'light'
}
